import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Slider } from "../ui/slider";
import { Wrench } from "lucide-react";

const formatNumber = (value, decimals = 0) => {
  if (isNaN(value) || !isFinite(value)) {
    return decimals > 0 ? (0).toFixed(decimals) : "0";
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export function RenovationOptionsSection({
  homeArea,
  renovationTier,
  setRenovationTier,
  addAreaCheckbox,
  setAddAreaCheckbox,
  extraArea,
  setExtraArea,
  extraAreaTier,
  setExtraAreaTier,
  rentalPeriod,
  setRentalPeriod,
  results,
  readOnly = false,
}) {
  const renovationTierDisplay =
    renovationTier > 499
      ? "Premium Renovation"
      : renovationTier > 399
      ? "Complete Renovation"
      : renovationTier > 299
      ? "Standard Renovation"
      : "Basic Renovation";

  return (
    <Card className="shadow-md">
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Wrench className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
          Renovation Options
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 space-y-4 sm:space-y-6">
        <div className="space-y-3">
          <Label htmlFor="renovation-tier" className="text-xs sm:text-sm block">
            Renovation of Existing Structure - ({formatNumber(homeArea)} sq.
            ft.) - (Upgrade or remodel an existing building.)
          </Label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Slider
              id="renovation-tier-slider"
              min={100}
              max={1000}
              step={10}
              value={[renovationTier]}
              onValueChange={(value) => setRenovationTier(value[0])}
              className="flex-1"
              disabled={readOnly}
            />
            <span className="text-sm sm:text-base font-semibold text-gray-700 sm:min-w-[140px] text-left sm:text-right">
              ${renovationTier} per sq. ft.
            </span>
          </div>
          <p className="text-xs sm:text-sm font-medium text-gray-700">
            {renovationTierDisplay}
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="add-area-checkbox"
              checked={addAreaCheckbox}
              onCheckedChange={setAddAreaCheckbox}
              disabled={readOnly}
            />
            <Label
              htmlFor="add-area-checkbox"
              className="text-xs sm:text-sm font-medium cursor-pointer"
            >
              Add Extra Area - Build entirely new structure apart from existing
              area
            </Label>
          </div>

          {addAreaCheckbox && (
            <div className="space-y-4 pl-0 sm:pl-6">
              <div className="space-y-2">
                <Label htmlFor="extra-area" className="text-xs sm:text-sm">
                  Extra Area to Build (sq ft)
                </Label>
                <Input
                  type="number"
                  id="extra-area"
                  value={extraArea}
                  onChange={(e) => setExtraArea(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="extra-area-tier" className="text-xs sm:text-sm">
                  Extra Area Cost
                </Label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <Slider
                    id="extra-area-tier-slider"
                    min={300}
                    max={1000}
                    step={10}
                    value={[extraAreaTier]}
                    onValueChange={(value) => setExtraAreaTier(value[0])}
                    className="flex-1"
                    disabled={readOnly}
                  />
                  <span className="text-sm sm:text-base font-semibold text-gray-700 sm:min-w-[140px] text-left sm:text-right">
                    ${extraAreaTier} per sq. ft.
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <p
          id="renovation-total"
          className="text-xs sm:text-sm text-gray-700 pt-2 border-t"
        >
          ${Number(results.constructionLoan).toLocaleString()} total renovation
          cost - Total Area:{" "}
          {formatNumber(parseInt(homeArea) + parseInt(extraArea))} sq ft
          {extraArea > 0
            ? ` (Existing Construction ${formatNumber(
                homeArea
              )} sq ft + New Construction ${formatNumber(extraArea)} sq ft)`
            : ""}
        </p>

        <div className="space-y-3 pt-2 border-t">
          <Label htmlFor="rental-period" className="text-xs sm:text-sm">
            Renovation Period plus Time to Sell
          </Label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Slider
              id="rental-period"
              min={6}
              max={36}
              step={1}
              value={[rentalPeriod]}
              onValueChange={(value) => setRentalPeriod(value[0])}
              className="flex-1"
              disabled={readOnly}
            />
            <span className="text-sm sm:text-base font-semibold text-gray-700 sm:min-w-[140px] text-left sm:text-right">
              {rentalPeriod} Months
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
